import React from 'react';

import { configuration } from '@/config';
import clarity from '@/services/clarity';

const hasClarity = !!configuration.clarityProjectId;

interface ClarityHook {
  /**
   * Custom Identifiers are informational data values about site visitors that are sent to Clarity by your client-side code over its Identify API. They include custom-id, custom-session-id, and custom-page-id and can help you customize the features on your site that requires it.
   * @param customId - The unique identifier for the customer
   * @param customSessionId - A custom session identifier
   * @param customPageId - A custom page identifier
   * @param friendlyName - A friendly name for the customer.
   */
  identify: (
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string,
  ) => void;
  /**
   * Clarity offers many predefined ways to filter and analyze website data. However, you might want to track elements specific to your site or user experience. With custom tags, you can apply arbitrary tags to your Clarity session.
   * @param key - The key for the tag.
   * @param value - The value(s) for the tag.
   */
  setTag: (key: string, value: string | string[]) => void;
  /**
   * If you prefer to instrument these user actions manually via Clarity APIs, call the event API with the action you'd like to track. When Clarity collects data for this event, it appears with your other Smart events in the Filters, Dashboard, Settings, and Recordings vertical.
   * @param eventName - The name of the event.
   */
  event: (eventName: string) => void;
  /**
   * You can use the upgrade API to prioritize specific types of sessions for recording. This is useful if you have sessions with specific types of events (such as clicks) that you want to look at or interactions with specific parts of your website (such as a shopping cart).
   * @param reason - The reason for the upgrade.
   */
  upgrade: (reason: string) => void;
}

export const useClarity = (): ClarityHook =>
  React.useMemo(
    (): ClarityHook => ({
      identify: (
        customId: string,
        customSessionId?: string,
        customPageId?: string,
        friendlyName?: string,
      ): void => {
        if (hasClarity) {
          clarity.identify(customId, customSessionId, customPageId, friendlyName);
        }
      },
      setTag: (key: string, value: string | string[]): void => {
        if (hasClarity) {
          clarity.setTag(key, value);
        }
      },
      event: (eventName: string): void => {
        if (hasClarity) {
          clarity.event(eventName);
        }
      },
      upgrade: (reason: string): void => {
        if (hasClarity) {
          clarity.upgrade(reason);
        }
      },
    }),
    [],
  );
